import { PlanningBoardSettings } from '@core/models/planning-board-settings';
import { NoRowsOverlayComponent } from '@portal-shared/app/components/no-rows-overlay/no-rows-overlay.component';
import { BreakPoints } from '@portal-shared/app/directives/responsive.const';
import { Period } from '@portal-shared/app/entities/period.model';
import { ValueSetterParamsT } from '@portal-shared/app/typings/value-setter-t.type';
import { TimerUtils } from '@portal-shared/app/utils/timer.utils';
import { AssignmentPeriodDto } from '@services/dtos/assignment-period.dto';
import { MinimalAssignmentPeriodDto } from '@services/dtos/rows/minimal-assignment-period.dto';
import { PeriodUsageDto } from '@services/dtos/rows/period-usage.dto';
import { PlanningBoardPeriodService } from '@services/planning-board-period.service';
import { PeriodHeaderCellRendererComponent } from '@shared/components/grids/header-cell-renderers/period-header-cell-renderer/period-header-cell-renderer.component';
import { AssignmentPeriodCellEditorComponent } from '@shared/components/grids/row-cell-renderers/assignment-period-cell-editor/assignment-period-cell-editor.component';
import { AssignmentPeriodCellRendererComponent } from '@shared/components/grids/row-cell-renderers/assignment-period-cell-renderer/assignment-period-cell-renderer.component';
import { BlankCellRendererComponent } from '@shared/components/grids/row-cell-renderers/blank-cell-renderer.component';
import { LoadingRowCellRendererComponent } from '@shared/components/grids/row-cell-renderers/loading-row-cell-renderer/loading-row-cell-renderer.component';
import { AssignmentRow } from '@shared/components/grids/rows/assignments/assignment-row';
import { PlanningBoardGroupRowType } from '@shared/components/grids/rows/groups/planning-board-group-row-type.enum';
import { PlanningGroupRow } from '@shared/components/grids/rows/groups/planning-group.row';
import { ClassHelper } from '@shared/helpers/class.helper';
import { GridNavigationHelper } from '@shared/helpers/grid-navigation.helper';
import {
  CellClassParams,
  CellRendererSelectorResult,
  ColDef,
  ColumnResizedEvent,
  GetGroupIncludeTotalRowParams,
  GetRowIdParams,
  GridOptions,
  ICellRendererParams,
  IRowNode,
  RowHeightParams
} from 'ag-grid-community';
import { PeriodColumnParams } from '../../core/typings/period-column-params';
import { PlanningRow } from '../../shared/components/grids/rows/planning-row';
import { GroupCellRendererFactory } from './group-cell-renderer.factory';
import { RowHeights } from './row-heights';

export abstract class PlanningGridConfig {
  public abstract readonly groupBy: Map<PlanningBoardGroupRowType, number>;
  protected abstract getAutoGroupHeaderComponent(): unknown;
  protected abstract getFooterComponent(): unknown;
  protected abstract saveAutoGroupColWidth: (params: ColumnResizedEvent) => Promise<void> | undefined;

  public static readonly autoColId = 'ag-Grid-AutoColumn'; // forced by ag-grid, cannot be modified.

  public static readonly columnConfigs = {
    minPeriodColumnCount: 3,
    maxPeriodColumnCount: 26, //half a year
    testPeriodColumnCount: 4, //default for testing
    pastPeriodColumnFactor: 0.25, //% of periods that will be past periods
    periodColumnMinWidth: 75, //px
    periodColumnNormalWidth: 250, //px
    groupColumnWidth: 300 //px
  };

  public static rowIndentConfig = {
    baseIndent: 12,
    indentPerLevel: 20
  };

  public gridOptions: GridOptions<PlanningRow> = {
    getRowHeight: params => this.getRowHeight(params),
    getRowClass: params => (params.node.group ? 'portal-group-row' : 'portal-leaf-row'),

    isExternalFilterPresent: () => true,

    headerHeight: RowHeights.planningGrid.header,

    defaultColDef: {
      flex: 1,
      minWidth: PlanningGridConfig.columnConfigs.periodColumnMinWidth,
      cellClass: ['portal-fill-center'],
      resizable: false,
      sortable: false
    },
    autoGroupColumnDef: this.getAutoGroupColumnDef(),
    animateRows: false,
    loading: false,
    suppressGroupRowsSticky: true,
    suppressAnimationFrame: false, // required for performance
    alwaysShowVerticalScroll: true,
    suppressHorizontalScroll: false,
    suppressMaintainUnsortedOrder: true,
    suppressAggFilteredOnly: true, // If disabled, the aggregated result could be false as it will only use visible
    deltaSort: true, //avoid resorting everything when we receive an update, only sort the update.

    // Server side options
    rowModelType: 'serverSide',
    suppressAggFuncInHeader: true,
    maintainColumnOrder: false,
    serverSideInitialRowCount: 6, // 6 fake loading rows at start
    blockLoadDebounceMillis: TimerUtils.humanThresholdMs,
    asyncTransactionWaitMillis: TimerUtils.debounceWebsocketMs,
    maxConcurrentDatasourceRequests: 3,
    serverSideEnableClientSideSort: true,
    serverSideSortAllLevels: false,
    serverSideOnlyRefreshFilteredGroups: false,
    cacheBlockSize: 50,
    // maxBlocksInCache: 100 // 100 * 100 = 10,000 rows max in cache, for performance reasons. Incompatible with dynamic getRowHeight()

    loadingCellRenderer: LoadingRowCellRendererComponent,
    noRowsOverlayComponent: NoRowsOverlayComponent,
    noRowsOverlayComponentParams: {
      i18nMessageKey: 'shared.grid.noData'
    },

    onColumnResized: async params => {
      if (params.finished && params.column?.getColId() === PlanningGridConfig.autoColId) {
        params.api.sizeColumnsToFit();
        await this.saveAutoGroupColWidth(params);
      }
    },

    processCellForClipboard: params => {
      const clipboardHasPlannedHours = (data: unknown): data is { plannedHours?: number } => {
        return data instanceof Object && 'plannedHours' in data;
      };

      if (clipboardHasPlannedHours(params.value)) return params.value.plannedHours;
      return undefined;
    },

    getRowId: (params: GetRowIdParams<PlanningRow>) => {
      const rowId = params.data.rowId;
      if (!rowId) throw new Error("RowIds can't be null or undefined");
      return rowId;
    },

    groupTotalRow: params => (this.groupTotalRow(params) ? 'bottom' : undefined)
  };

  public getNavigationGridOptions(planningBoardPeriodService: PlanningBoardPeriodService): GridOptions<PlanningRow> {
    return {
      tabToNextCell: params =>
        GridNavigationHelper.MoveCellPosition(
          planningBoardPeriodService,
          params.previousCellPosition,
          params.nextCellPosition
        ) ?? true, //ag-grid v31.3, return true to stay on the current cell, null deprecated.
      tabToNextHeader: params =>
        GridNavigationHelper.MoveCellPosition(
          planningBoardPeriodService,
          params.previousHeaderPosition,
          params.nextHeaderPosition
        ) ?? true, //ag-grid v31.3, return true to stay on the current cell, null deprecated.
      navigateToNextCell: params =>
        GridNavigationHelper.MoveCellPosition(
          planningBoardPeriodService,
          params.previousCellPosition,
          params.nextCellPosition
        ),
      navigateToNextHeader: params =>
        GridNavigationHelper.MoveCellPosition(
          planningBoardPeriodService,
          params.previousHeaderPosition,
          params.nextHeaderPosition
        )
    };
  }

  public static readonly isResourceColumnPinned: boolean | 'left' | 'right' | null =
    screen.width >= BreakPoints.Tablet ? 'left' : null;

  public getAutoGroupColumnDef(): ColDef<PlanningRow> {
    return {
      colId: PlanningGridConfig.autoColId,
      sortable: true,
      sort: 'asc',
      suppressPaste: true,
      resizable: true,
      suppressMovable: true,
      suppressNavigable: true,
      editable: false,
      suppressSizeToFit: true,
      enableCellChangeFlash: false,
      flex: 0,

      pinned: PlanningGridConfig.isResourceColumnPinned,
      width: PlanningGridConfig.columnConfigs.groupColumnWidth,
      minWidth: 0.5 * PlanningGridConfig.columnConfigs.groupColumnWidth,
      maxWidth: 2 * PlanningGridConfig.columnConfigs.groupColumnWidth,
      cellRendererParams: {
        innerRendererSelector: (params: ICellRendererParams<PlanningRow>) => {
          if (params.node.footer) {
            return { component: this.getFooterComponent(), params };
          }
          if (params.node.group) {
            const row = params.data as PlanningGroupRow;
            const component = GroupCellRendererFactory.getGroupCellRenderer(row.type);
            return component ? { component, params } : undefined;
          }
          return this.leafCellRendererSelector(params);
        },
        suppressCount: true,
        showRowGroup: true
      },

      comparator: (_valueA, _valueB, nodeA: IRowNode<PlanningRow>, nodeB: IRowNode<PlanningRow>) =>
        PlanningGridConfig.compare(nodeA, nodeB),

      cellStyle: params => {
        const paddingLeft = PlanningGridConfig.getCellIndent(params.node.level);
        return { paddingLeft: `${paddingLeft}px` };
      },
      cellClass: params => {
        const classes = this.getGroupColumnCellClasses(params).concat(PlanningGridConfig.getClasses(params.node));
        let parent = params.node.parent;
        while (parent) {
          classes.push(...PlanningGridConfig.getClasses(parent));
          parent = parent.parent;
        }
        return classes; // Return the classes of the node and all its parents
      },

      valueGetter: params => params.data,

      headerComponent: this.getAutoGroupHeaderComponent()
    };
  }

  public getPeriodColumnDefs(
    periods: Period[],
    planningBoardSettings: PlanningBoardSettings,
    updateCellCb: (
      params: ValueSetterParamsT<AssignmentRow, MinimalAssignmentPeriodDto | undefined, string | undefined>
    ) => boolean
  ) {
    return periods.map((period, periodIndex) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const getPeriod = () => periods[periodIndex];

      const periodColumnParams: PeriodColumnParams = {
        periodIndex,
        period,
        planningBoardSettings
      };

      const col: ColDef<PlanningRow, AssignmentPeriodDto | PeriodUsageDto> = {
        flex: 1,
        colId: `${period.id}`,
        cellEditor: AssignmentPeriodCellEditorComponent,
        cellRendererSelector: params => PlanningGridConfig.periodCellRendererSelector(params, getPeriod()),
        cellRendererParams: periodColumnParams,

        headerComponent: PeriodHeaderCellRendererComponent,
        headerComponentParams: periodColumnParams,
        suppressMovable: true,
        enableCellChangeFlash: false,

        valueGetter: ({ data: boardRow }) =>
          boardRow?.getValueOrDefault(getPeriod().id) as AssignmentPeriodDto | PeriodUsageDto,

        // We can cast the params on the setter because only AssignmentRows are editable
        valueSetter: params =>
          updateCellCb(
            params as unknown as ValueSetterParamsT<AssignmentRow, MinimalAssignmentPeriodDto | undefined, string>
          ),

        cellClassRules: { 'al-assignment-period-cell': (params: CellClassParams) => !params.node.group },

        editable: params => (params.data as AssignmentRow | undefined)?.periodEditable(getPeriod()) ?? false,

        cellClass: (params: CellClassParams<PlanningRow, AssignmentPeriodDto | PeriodUsageDto>) => {
          const classes = ['portal-fill-center', 'portal-padding-0', ClassHelper.periodIndex(periodIndex)];
          classes.push(...PlanningGridConfig.getClasses(params.node));

          let parent = params.node.parent;
          while (parent) {
            classes.push(...PlanningGridConfig.getClasses(parent));
            parent = parent.parent;
          }
          return classes; // Return the classes of the node and all its parents
        }
      };
      return col;
    });
  }

  public static compare(nodeA: IRowNode<PlanningRow>, nodeB: IRowNode<PlanningRow>) {
    if (nodeA.group || nodeB.group) return 0; // Groups sort is only handled by the server

    const rowA = nodeA.data as AssignmentRow | undefined;
    if (!rowA) return -1;

    const rowB = nodeB.data as AssignmentRow | undefined;
    if (!rowB) return 1;

    return rowA.compare(rowB);
  }

  public static getCellIndent(level: number) {
    const { baseIndent, indentPerLevel } = this.rowIndentConfig;
    return baseIndent + level * indentPerLevel;
  }

  public static getClasses(node: IRowNode<PlanningRow>) {
    return node.data?.styleClasses ?? [];
  }

  public abstract leafCellRendererSelector(
    params: ICellRendererParams<PlanningRow>
  ): CellRendererSelectorResult | undefined;

  public getRowHeight(params: RowHeightParams<PlanningRow>) {
    if (params.node.footer) return RowHeights.planningGrid.footer;

    if (!params.data) return RowHeights.planningGrid.loader;

    return params.data?.height ?? RowHeights.planningGrid.rowSmall;
  }

  public static periodCellRendererSelector = (
    params: ICellRendererParams<PlanningRow, PeriodUsageDto | AssignmentPeriodDto>,
    period: Period
  ): CellRendererSelectorResult | undefined => {
    if (params.node.footer) return { component: BlankCellRendererComponent, params };

    if (params.node.group && params.node.field) {
      const row = params.data as PlanningGroupRow | undefined;

      if (!row) return undefined;

      const component = GroupCellRendererFactory.getPeriodCellRenderer(row.type, period);

      return component ? { component, params } : undefined;
    }

    return { component: AssignmentPeriodCellRendererComponent, params };
  };

  public groupTotalRow(_params: GetGroupIncludeTotalRowParams) {
    return false;
  }

  public getGroupColumnCellClasses(_params: CellClassParams<PlanningRow, unknown>): string[] {
    return [];
  }
}
